import { useFlag } from '@unleash/proxy-client-react';
import React from 'react';
import NewSessionContextProvider from '../NewSessionContextProvider';
import EndSessionDialogWrapper from '../SessionContext/EndSessionDialogWrapper';
import SessionCardPinContextProvider from '../SessionContext/SessionCardPinContextProvider';
import useSessionCardPinContext from '../SessionContext/SessionCardPinContextProvider/useSessionCardPinContext';
import SessionComplianceContextProvider from '../SessionContext/SessionComplianceContextProvider';
import useSessionComplianceContext from '../SessionContext/SessionComplianceContextProvider/useSessionComplianceContext';
import SessionConnectionContextProvider from '../SessionContext/SessionConnectionContextProvider';
import SessionContextContextProvider from '../SessionContext/SessionContextContextProvider';
import useSessionContextContext from '../SessionContext/SessionContextContextProvider/useSessionContextContext';
import SessionDetectionContextProvider from '../SessionContext/SessionDetectionContextProvider';
import useSessionDetectionContext from '../SessionContext/SessionDetectionContextProvider/useSessionDetectionContext';
import SessionInfoContextProvider from '../SessionContext/SessionInfoContextProvider';
import useSessionInfoContext from '../SessionContext/SessionInfoContextProvider/useSessionInfoContext';
import SessionLanguageContextProvider from '../SessionContext/SessionLanguageContextProvider/SessionLanguageContextProvider';
import SessionManagerContextProvider from '../SessionContext/SessionManagerContextProvider';
import useSessionManagerContext from '../SessionContext/SessionManagerContextProvider/useSessionManagerContext';
import { SessionContext } from './useSessionContext';

function Inner({ children }: { children: React.ReactNode }) {
  const { sessionId, session, setEndCause } = useSessionInfoContext();
  const { startSession, sessionLoading } = useSessionManagerContext();
  const { pinnedAt, pin, unpin } = useSessionCardPinContext();
  const compliance = useSessionComplianceContext();
  const { latestDetection } = useSessionDetectionContext();
  const { context, setContext } = useSessionContextContext();

  const value = React.useMemo(() => ({
    sessionId,
    session,
    sessionLoading,
    startSession,
    stopSession() { setEndCause('ManualSessionTermination'); },
    context,
    setContext,
    pinnedAt,
    pin,
    unpin,
    compliance,
    latestDetection,
  }), [sessionId, session, sessionLoading, startSession, setEndCause, context, setContext, pinnedAt, pin, unpin, compliance, latestDetection]);

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>

  );
}

export default function SessionContextProvider({ children }: { children: React.ReactNode }) {
  const flag = useFlag('enable-dual-canal');

  if (flag) {
    return <NewSessionContextProvider>{children}</NewSessionContextProvider>;
  }

  return (
    <SessionInfoContextProvider>
      <SessionLanguageContextProvider>
        <SessionManagerContextProvider>
          <SessionCardPinContextProvider>
            <SessionContextContextProvider>
              <SessionDetectionContextProvider>
                <SessionConnectionContextProvider>
                  <SessionComplianceContextProvider>
                    <Inner>
                      <EndSessionDialogWrapper>
                        {children}
                      </EndSessionDialogWrapper>
                    </Inner>
                  </SessionComplianceContextProvider>
                </SessionConnectionContextProvider>
              </SessionDetectionContextProvider>
            </SessionContextContextProvider>
          </SessionCardPinContextProvider>
        </SessionManagerContextProvider>
      </SessionLanguageContextProvider>
    </SessionInfoContextProvider>
  );
}
